import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import {throwError as observableThrowError, lastValueFrom } from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { ApiHelperService } from './api-helper.service';
import { GenericAPIArrayResponse, GenericAPIResponse, LoginApiResponse } from '../models/api-response';
import { ClipView, ClipViewAnalytics, ClipViewAnalyticsBucket, ClipViewData, ClipViewPostResult, TeamViewAnalytics, User, WatchTimeData } from '../models/user';

@Injectable({
  providedIn: 'root'
})
export class ClipviewsService {

  constructor(
    private http: HttpClient,
    private apiHelper: ApiHelperService,
    private router: Router
  ) { }

  postClipViews(clipviews: ClipView[]): Promise<GenericAPIResponse<ClipViewPostResult>> {
    const body = { clipviews };
    const url = `${this.apiHelper.apiUrl()}/view-stats`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<ClipViewPostResult>>(url, body, { headers }).pipe(
      map((res) => res),
      catchError((err) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getTeamWeekWatchtimes(startDate: string, endDate: string): Promise<GenericAPIResponse<WatchTimeData>> {
    const url = `${this.apiHelper.apiUrl()}/view-stats/teams`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = { startDate, endDate };

    return lastValueFrom(this.http.post<GenericAPIResponse<WatchTimeData>>(url, body, { headers }).pipe(
      map((res) => res),
      catchError((err) => {
        return observableThrowError(err.message);
      }),));
  }

  getUserWeekWatchtimes(userid: string, startDate: string, endDate: string): Promise<GenericAPIArrayResponse<ClipViewData>> {
    const url = `${this.apiHelper.apiUrl()}/view-stats/users/${userid}`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = { startDate, endDate };

    return lastValueFrom(this.http.post<GenericAPIArrayResponse<ClipViewData>>(url, body,{ headers }).pipe(
      map((res) => res),
      catchError((err) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getUserViewAnalytics(userid: string, rangeStartDate: string, rangeEndDate: string, bucket: ClipViewAnalyticsBucket, timezone: string): Promise<GenericAPIResponse<ClipViewAnalytics>> {
    const url = `${this.apiHelper.apiUrl()}/view-stats/users/${userid}/analytics`;
    const body = {rangeStartDate, rangeEndDate, bucket, timezone};
    return lastValueFrom(this.http.post<GenericAPIResponse<ClipViewAnalytics>>(url, body, {headers: this.apiHelper.apiHeaderWithToken()}).pipe(
      map(res => res),
      catchError(err => observableThrowError(err.message)),
    ));
  }

  getTeamViewAnalytics(rangeStartDate: string, rangeEndDate: string, bucket: ClipViewAnalyticsBucket, timezone: string): Promise<GenericAPIResponse<TeamViewAnalytics>> {
    const url = `${this.apiHelper.apiUrl()}/view-stats/teams/analytics`;
    const body = {rangeStartDate, rangeEndDate, bucket, timezone};
    return lastValueFrom(this.http.post<GenericAPIResponse<TeamViewAnalytics>>(url, body, {headers: this.apiHelper.apiHeaderWithToken()}).pipe(
      map(res => res),
      catchError(err => observableThrowError(err.message)),
    ));
  }

  getTeamTopViewers(teamid: string, startDate?: string, endDate?: string, includeAllUsers: boolean = false): Promise<GenericAPIResponse<{coaches: {user: User, timewatched: number, clips: number}[], athletes: {user: User, timewatched: number, clips: number}[]}>> {
    const url = `${this.apiHelper.apiUrl()}/view-stats/team-top-viewers/${teamid}`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = { startDate, endDate, includeAllUsers };

    return lastValueFrom(this.http.post<GenericAPIResponse<{coaches: {user: User, timewatched: number, clips: number}[], athletes: {user: User, timewatched: number, clips: number}[]}>>(url, body, { headers }).pipe(
      map((res) => res),
      catchError((err) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getTeamMostWatchedGames(teamid: string): Promise<GenericAPIArrayResponse<{gameid: string, timewatched: number, matchup: string, gamedate: Date}>> {
    const url = `${this.apiHelper.apiUrl()}/view-stats/team-top-games/${teamid}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<{gameid: string, timewatched: number, matchup: string, gamedate: Date}>>(url, { headers }).pipe(
      map((res) => res),
      catchError((err) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }
}
